import { cn } from "@/ds/utils";
import { useMemo } from "react";
import { type EmblemRarity } from "@/lib/domain/EmblemRarity";
import accentGolden from "@/assets/emblems/GOLDEN_ACCENT.png";
import borderCommon from "@/assets/emblems/borders/BORDER_COMMON.png";
import borderRare from "@/assets/emblems/borders/BORDER_RARE.png";
import borderMajestic from "@/assets/emblems/borders/BORDER_MAJESTIC.png";
import borderLegendary from "@/assets/emblems/borders/BORDER_LEGENDARY.png";
import borderTaylor from "@/assets/emblems/borders/BORDER_TAYLOR.png";
import borderPinkVisor from "@/assets/emblems/borders/BORDER_PINK_VISOR.png";
import borderPoppedCollarPolo from "@/assets/emblems/borders/BORDER_POPPED_COLLAR_POLO.png";
import borderFabled from "@/assets/emblems/borders/BORDER_FABLED.png";
import borderFabled2 from "@/assets/emblems/borders/BORDER_FABLED_2.png";
import borderFabled3 from "@/assets/emblems/borders/BORDER_FABLED_3.png";

export interface EmblemProps {
	className?: string;
	emblemId: string;
	rarity: EmblemRarity;
	count: number;
	onClick?: () => void;
}

export const Emblem = (props: EmblemProps): React.JSX.Element => {
	const {
		className,
		emblemId,
		rarity,
		count,
		onClick
	} = props;

	const imageSrc = useMemo(() => getImageForEmblemId(emblemId), [emblemId]);
	const borderSrc = useMemo(() => getBorder(emblemId, rarity), [emblemId, rarity]);

	return (
		<div className={cn(className, "relative aspect-square")} onClick={onClick}>
			{ count >= getGoldenThresholdForEmblemRarity(rarity) && <img src={accentGolden} className="absolute top-0 left-0 w-full" />}
			<img src={imageSrc} className="absolute top-0 left-0 w-full" />
			<img src={borderSrc} className="absolute top-0 left-0 w-full" />
		</div>
	);
};

const getBorder = (emblemId: string, rarity: EmblemRarity): string => {
	if (emblemId === "EYE_OF_OPHIDIA") {
		return borderFabled2;
	}

	if (emblemId === "ARKNIGHT_SHARD") {
		return borderFabled3;
	}

	if (emblemId === "TAYLOR") {
		return borderTaylor;
	}

	if (emblemId === "PINK_VISOR") {
		return borderPinkVisor;
	}

	if (emblemId === "POPPED_COLLAR_POLO") {
		return borderPoppedCollarPolo;
	}

	switch (rarity) {
	case "Special":
		return borderTaylor;
	case "Rare":
		return borderRare;
	case "Majestic":
		return borderMajestic;
	case "Legendary":
		return borderLegendary;
	case "Fabled":
		return borderFabled;
	default:
		return borderCommon;
	}
};

export const getGoldenThresholdForEmblemRarity = (rarity: EmblemRarity): number => {
	switch (rarity) {
	case "Rare":
		return 5;
	case "Majestic":
		return 4;
	case "Legendary":
		return 3;
	case "Fabled":
		return 2;
	default:
		return 10;
	}
};

const getImageForEmblemId = (emblemId: string): string => {
	switch (emblemId) {
	case "TAYLOR":
		return image_TAYLOR;
	case "PINK_VISOR":
		return image_PINK_VISOR;
	case "POPPED_COLLAR_POLO":
		return image_POPPED_COLLAR_POLO;

	case "HEART_OF_FYENDAL":
		return image_HEART_OF_FYENDAL;
	case "FYENDALS_SPRING_TUNIC":
		return image_FYENDALS_SPRING_TUNIC;
	case "EMPEROR_DRACAI_OF_AESIR":
		return image_EMPEROR_DRACAI_OF_AESIR;
	case "LEVIA_REDEEMED":
		return image_LEVIA_REDEEMED;
	case "TEKLOVOSSEN_THE_MECHROPOTENT":
		return image_TEKLOVOSSEN_THE_MECHROPOTENT;
	case "ENIGMA_NEW_MOON":
		return image_ENIGMA_NEW_MOON;
	case "ENLIGHTENED_STRIKE":
		return image_ENLIGHTENED_STRIKE;
	case "ART_OF_WAR":
		return image_ART_OF_WAR;
	case "COMMAND_AND_CONQUER":
		return image_COMMAND_AND_CONQUER;
	case "CHANNEL_LAKE_FRIGID":
		return image_CHANNEL_LAKE_FRIGID;
	case "TOMELTAI":
		return image_TOMELTAI;
	case "GAMBLERS_GLOVE":
		return image_GAMBLERS_GLOVE;
	case "NOURISHING_EMPTINESS":
		return image_NOURISHING_EMPTINESS;
	case "ALPHA_RAMPAGE":
		return image_ALPHA_RAMPAGE;
	case "AETHER_WILDFIRE":
		return image_AETHER_WILDFIRE;
	case "LEAVE_NO_WITNESSES":
		return image_LEAVE_NO_WITNESSES;
	case "RIPTIDE":
		return image_RIPTIDE;
	case "DORINTHEA":
		return image_DORINTHEA;
	case "PRISM":
		return image_PRISM;
	case "DROMAI":
		return image_DROMAI;
	case "LEVIA":
		return image_LEVIA;
	case "KAYO":
		return image_KAYO;
	case "OLDHIM":
		return image_OLDHIM;
	case "VYNNSET":
		return image_VYNNSET;
	case "BRAVO":
		return image_BRAVO;
	case "IYSLANDER":
		return image_IYSLANDER;
	case "KASSAI":
		return image_KASSAI;
	case "LEXI":
		return image_LEXI;
	case "BRIAR":
		return image_BRIAR;
	case "KAVDAEN":
		return image_KAVDAEN;
	case "ARAKNI":
		return image_ARAKNI;
	case "CHANE":
		return image_CHANE;
	case "BENJI":
		return image_BENJI;
	case "FAI":
		return image_FAI;
	case "OLYMPIA":
		return image_OLYMPIA;
	case "VICTOR":
		return image_VICTOR;
	case "ANOTHOS":
		return image_ANOTHOS;
	case "CINTARI_SABER":
		return image_CINTARI_SABER;
	case "DAWNBLADE":
		return image_DAWNBLADE;
	case "HARMONIZED_KODACHI":
		return image_HARMONIZED_KODACHI;
	case "MANDIBLE_CLAW":
		return image_MANDIBLE_CLAW;
	case "ROMPING_CLUB":
		return image_ROMPING_CLUB;
	case "TEKLO_PLASMA_PISTOL":
		return image_TEKLO_PLASMA_PISTOL;
	case "DEATH_DEALER":
		return image_DEATH_DEALER;
	case "ROSETTA_THORN":
		return image_ROSETTA_THORN;
	case "SEARING_EMBERBLADE":
		return image_SEARING_EMBERBLADE;
	case "HELM_OF_ISENS_PEAK":
		return image_HELM_OF_ISENS_PEAK;
	case "SINK_BELOW":
		return image_SINK_BELOW;
	case "SNAPDRAGON_SCALERS":
		return image_SNAPDRAGON_SCALERS;
	case "FATE_FORSEEN":
		return image_FATE_FORSEEN;
	case "RAVENOUS_RABLE":
		return image_RAVENOUS_RABLE;
	case "HALO_OF_ILLUMINATION":
		return image_HALO_OF_ILLUMINATION;
	case "RAGAMUFFINS_HAT":
		return image_RAGAMUFFINS_HAT;
	case "COPPER":
		return image_COPPER;
	case "SPECTRAL_SHIELD":
		return image_SPECTRAL_SHIELD;
	case "COLD_SNAP":
		return image_COLD_SNAP;
	case "SCAR_FOR_A_SCAR":
		return image_SCAR_FOR_A_SCAR;
	case "CROUCHING_TIGER":
		return image_CROUCHING_TIGER;
	case "WATER_GLOW_LANTERNS":
		return image_WATER_GLOW_LANTERNS;
	case "BIG_BERTHA":
		return image_BIG_BERTHA;
	case "GOBLET_OF_BLOODRUN_WINE":
		return image_GOBLET_OF_BLOODRUN_WINE;
	case "WILD_RIDE":
		return image_WILD_RIDE;
	case "FIRST_TENET_OF_CHI_TIDE":
		return image_FIRST_TENET_OF_CHI_TIDE;
	case "STIR_THE_POT":
		return image_STIR_THE_POT;
	case "THE_GRAIN_THAT_TIPS_THE_SCALE":
		return image_THE_GRAIN_THAT_TIPS_THE_SCALE;

	case "EYE_OF_OPHIDIA":
		return image_EYE_OF_OPHIDIA;
	case "COMMAND_AND_CONQUER_2":
		return image_COMMAND_AND_CONQUER_2;
	case "DEAD_THREADS":
		return image_DEAD_THREADS;
	case "POLLY_CRANKA":
		return image_POLLY_CRANKA;
	case "SHIYANA_DIAMOND_GEMINI":
		return image_SHIYANA_DIAMOND_GEMINI;
	case "CROWN_OF_PROVIDENCE":
		return image_CROWN_OF_PROVIDENCE;
	case "CODEX_OF_FRAILTY":
		return image_CODEX_OF_FRAILTY;
	case "CONQUEROR_OF_THE_HIGH_SEAS":
		return image_CONQUEROR_OF_THE_HIGH_SEAS;
	case "DOWN_AND_DIRTY":
		return image_DOWN_AND_DIRTY;
	case "DUST_FROM_THE_GOLDEN_PLAINS":
		return image_DUST_FROM_THE_GOLDEN_PLAINS;
	case "ERASE_FACE":
		return image_ERASE_FACE;
	case "FUNERAL_MOON":
		return image_FUNERAL_MOON;
	case "JUDGE_JURY_EXECUTIONER":
		return image_JUDGE_JURY_EXECUTIONER;
	case "RUBY_AMULET":
		return image_RUBY_AMULET;
	case "SACRED_ART_UNDERCURRENT_DESIRES":
		return image_SACRED_ART_UNDERCURRENT_DESIRES;
	case "TRUCE":
		return image_TRUCE;
	case "AURORA":
		return image_AURORA;
	case "AZALEA":
		return image_AZALEA;
	case "BETSY":
		return image_BETSY;
	case "CINDRA":
		return image_CINDRA;
	case "FANG":
		return image_FANG;
	case "FLORIAN":
		return image_FLORIAN;
	case "GENIS_WOTCHUNEED":
		return image_GENIS_WOTCHUNEED;
	case "GRAVY_BONES":
		return image_GRAVY_BONES;
	case "IRA":
		return image_IRA;
	case "KANO":
		return image_KANO;
	case "MAXX_NITRO":
		return image_MAXX_NITRO;
	case "NUU":
		return image_NUU;
	case "OSCILIO":
		return image_OSCILIO;
	case "PUFFIN":
		return image_PUFFIN;
	case "SQUIZZY_AND_FLOOF":
		return image_SQUIZZY_AND_FLOOF;
	case "TEKLOVOSSEN":
		return image_TEKLOVOSSEN;
	case "UZURI":
		return image_UZURI;
	case "VISERAI":
		return image_VISERAI;
	case "YOJI":
		return image_YOJI;
	case "ZEN":
		return image_ZEN;
	case "A_DROP_IN_THE_OCEAN":
		return image_A_DROP_IN_THE_OCEAN;
	case "AETHER_FLARE":
		return image_AETHER_FLARE;
	case "ANGRY_BONES":
		return image_ANGRY_BONES;
	case "BARBED_CASTAWAY":
		return image_BARBED_CASTAWAY;
	case "BATTALION_BARQUE":
		return image_BATTALION_BARQUE;
	case "BESEECH_THE_DEMIGON":
		return image_BESEECH_THE_DEMIGON;
	case "BITING_BREEZE":
		return image_BITING_BREEZE;
	case "CELESTIAL_REPRIMAND":
		return image_CELESTIAL_REPRIMAND;
	case "CHART_A_COURSE":
		return image_CHART_A_COURSE;
	case "CONSUMING_VOLITION":
		return image_CONSUMING_VOLITION;
	case "DESIRES_OF_FLESH":
		return image_DESIRES_OF_FLESH;
	case "DRY_POWDER_SHOT":
		return image_DRY_POWDER_SHOT;
	case "EXPEDITION_TO_AZURO_KEYS":
		return image_EXPEDITION_TO_AZURO_KEYS;
	case "FIRST_TENET_OF_CHI_MOON":
		return image_FIRST_TENET_OF_CHI_MOON;
	case "FYENDALS_FIGHTING_SPIRIT":
		return image_FYENDALS_FIGHTING_SPIRIT;
	case "HATCHET_OF_BODY":
		return image_HATCHET_OF_BODY;
	case "HEAD_SHOT":
		return image_HEAD_SHOT;
	case "HEAD_STONE":
		return image_HEAD_STONE;
	case "IRIS_OF_REALITY":
		return image_IRIS_OF_REALITY;
	case "KUNAI_OF_RETRIBUTION":
		return image_KUNAI_OF_RETRIBUTION;
	case "MASK_OF_WIZENED_WHISKERS":
		return image_MASK_OF_WIZENED_WHISKERS;
	case "POUNCING_QI":
		return image_POUNCING_QI;
	case "RAVENOUS_MEATAXE":
		return image_RAVENOUS_MEATAXE;
	case "ROTWOOD_REAPER":
		return image_ROTWOOD_REAPER;
	case "SHIVER":
		return image_SHIVER;
	case "SIGIL_OF_LIGHTNING":
		return image_SIGIL_OF_LIGHTNING;
	case "STAR_FALL":
		return image_STAR_FALL;
	case "STORM_OF_SANDIKAI":
		return image_STORM_OF_SANDIKAI;
	case "TITANS_FIST":
		return image_TITANS_FIST;
	case "WATER_THE_SEEDS":
		return image_WATER_THE_SEEDS;

	case "ARKNIGHT_SHARD":
		return image_ARKNIGHT_SHARD;
	case "MASK_OF_DECEIT":
		return image_MASK_OF_DECEIT;
	case "CIRCLET_OF_ETERNAL_END":
		return image_CIRCLET_OF_ETERNAL_END;
	case "COMMAND_AND_CONQUER_2026":
		return image_COMMAND_AND_CONQUER_2026;
	case "NEW_HORIZON":
		return image_NEW_HORIZON;
	case "GHOSTLY_TOUCH":
		return image_GHOSTLY_TOUCH;
	case "DIMENXXIONAL_VORTEX":
		return image_DIMENXXIONAL_VORTEX;
	case "BRAINSTORM":
		return image_BRAINSTORM;
	case "ANNIHILATOR_ENGINE":
		return image_ANNIHILATOR_ENGINE;
	case "ART_OF_THE_DRAGON_CLAW":
		return image_ART_OF_THE_DRAGON_CLAW;
	case "ACT_OF_GLORY":
		return image_ACT_OF_GLORY;
	case "ATTUNE_WITH_COSMIC_VIBRATIONS":
		return image_ATTUNE_WITH_COSMIC_VIBRATIONS;
	case "CARESS_OF_THE_REAPER":
		return image_CARESS_OF_THE_REAPER;
	case "COAX_A_COMMOTION":
		return image_COAX_A_COMMOTION;
	case "BOLTING_BLADE":
		return image_BOLTING_BLADE;
	case "DIMENXXIONAL_CROSSROADS":
		return image_DIMENXXIONAL_CROSSROADS;
	case "ECHO_CASQUE":
		return image_ECHO_CASQUE;
	case "BANNERET_OF_RESILIENCE":
		return image_BANNERET_OF_RESILIENCE;
	case "BLESSING_OF_SPIRITS":
		return image_BLESSING_OF_SPIRITS;
	case "BASK_IN_YOUR_OWN_GREATNESS":
		return image_BASK_IN_YOUR_OWN_GREATNESS;
	case "BAIT":
		return image_BAIT;
	case "ART_OF_THE_DRAGON_SCALE":
		return image_ART_OF_THE_DRAGON_SCALE;
	case "AGILITY_STANCE":
		return image_AGILITY_STANCE;
	case "ANNEXATION_OF_ALL_THINGS_KNOWN":
		return image_ANNEXATION_OF_ALL_THINGS_KNOWN;
	case "BLANCH":
		return image_BLANCH;
	case "BENEATH_THE_SURFACE":
		return image_BENEATH_THE_SURFACE;
	case "ARC_BENDING":
		return image_ARC_BENDING;
	case "BLADE_BECKONER_HELM":
		return image_BLADE_BECKONER_HELM;
	case "COPPER_COG":
		return image_COPPER_COG;
	case "CLAP_EM_IN_IRONS":
		return image_CLAP_EM_IN_IRONS;
	case "FOOLS_GOLD":
		return image_FOOLS_GOLD;
	case "ARCANE_LANTERN":
		return image_ARCANE_LANTERN;
	case "BLUSTER_BUFF":
		return image_BLUSTER_BUFF;
	case "FIDDLE_DEE":
		return image_FIDDLE_DEE;
	case "AETHER_SINK":
		return image_AETHER_SINK;
	case "BLACKOUT_KICK":
		return image_BLACKOUT_KICK;
	case "BACKUP_PROTOCOL_RED":
		return image_BACKUP_PROTOCOL_RED;
	case "ANNIHILATE_THE_ARMED":
		return image_ANNIHILATE_THE_ARMED;
	case "APHRODIAS":
		return image_APHRODIAS;
	case "VANTOM_BANSHEE":
		return image_VANTOM_BANSHEE;
	case "SOUL_BUTCHER":
		return image_SOUL_BUTCHER;
	case "TEAR_THROUGH_THE_PORTAL":
		return image_TEAR_THROUGH_THE_PORTAL;
	case "WITHSTAND":
		return image_WITHSTAND;
	case "SKY_FIRE_LANTERNS":
		return image_SKY_FIRE_LANTERNS;
	case "SPECTRAL_RIDER":
		return image_SPECTRAL_RIDER;
	case "WANING_VENGEANCE":
		return image_WANING_VENGEANCE;
	case "SPILLOVER":
		return image_SPILLOVER;
	case "ZOOM_IN":
		return image_ZOOM_IN;
	case "STABBING_PAIN":
		return image_STABBING_PAIN;
	case "VOW_OF_VENGEANCE":
		return image_VOW_OF_VENGEANCE;
	case "STARLIGHT_ROAD":
		return image_STARLIGHT_ROAD;
	case "VIRULENT_TOUCH":
		return image_VIRULENT_TOUCH;
	case "SPRING_LOAD":
		return image_SPRING_LOAD;
	case "BILLOWING_MIST":
		return image_BILLOWING_MIST;
	case "SPREADING_MIST":
		return image_SPREADING_MIST;
	case "TWO_SIDES_TO_THE_BLADE":
		return image_TWO_SIDES_TO_THE_BLADE;
	case "STROKE_OF_FORESIGHT":
		return image_STROKE_OF_FORESIGHT;
	case "BANDANA_OF_THE_BLUE_BEYOND":
		return image_BANDANA_OF_THE_BLUE_BEYOND;
	case "WALK_THE_PLANK":
		return image_WALK_THE_PLANK;
	case "SWIFTWATER_SLOOP":
		return image_SWIFTWATER_SLOOP;
	case "WEE_WRECKING_BALL":
		return image_WEE_WRECKING_BALL;
	case "ARENA_MEDIC":
		return image_ARENA_MEDIC;
	case "TO_BE_CONTINUED":
		return image_TO_BE_CONTINUED;
	case "AMPLIFY_THE_ARKNIGHT":
		return image_AMPLIFY_THE_ARKNIGHT;
	case "SONG_OF_THE_ROSEN_MATADOR":
		return image_SONG_OF_THE_ROSEN_MATADOR;
	case "SURGING_MILITIA":
		return image_SURGING_MILITIA;

	default:
		return image_CRACKED_BAUBLE;
	}
};

import image_TAYLOR from "@/assets/emblems/images/TAYLOR.png";
import image_PINK_VISOR from "@/assets/emblems/images/PINK_VISOR.png";
import image_POPPED_COLLAR_POLO from "@/assets/emblems/images/POPPED_COLLAR_POLO.png";

import image_HEART_OF_FYENDAL from "@/assets/emblems/images/HEART_OF_FYENDAL.png";
import image_FYENDALS_SPRING_TUNIC from "@/assets/emblems/images/FYENDALS_SPRING_TUNIC.png";
import image_EMPEROR_DRACAI_OF_AESIR from "@/assets/emblems/images/EMPEROR_DRACAI_OF_AESIR.png";
import image_LEVIA_REDEEMED from "@/assets/emblems/images/LEVIA_REDEEMED.png";
import image_TEKLOVOSSEN_THE_MECHROPOTENT from "@/assets/emblems/images/TEKLOVOSSEN_THE_MECHROPOTENT.png";
import image_ENIGMA_NEW_MOON from "@/assets/emblems/images/ENIGMA_NEW_MOON.png";
import image_ENLIGHTENED_STRIKE from "@/assets/emblems/images/ENLIGHTENED_STRIKE.png";
import image_ART_OF_WAR from "@/assets/emblems/images/ART_OF_WAR.png";
import image_COMMAND_AND_CONQUER from "@/assets/emblems/images/COMMAND_AND_CONQUER.png";
import image_CHANNEL_LAKE_FRIGID from "@/assets/emblems/images/CHANNEL_LAKE_FRIGID.png";
import image_TOMELTAI from "@/assets/emblems/images/TOMELTAI.png";
import image_GAMBLERS_GLOVE from "@/assets/emblems/images/GAMBLERS_GLOVE.png";
import image_NOURISHING_EMPTINESS from "@/assets/emblems/images/NOURISHING_EMPTINESS.png";
import image_ALPHA_RAMPAGE from "@/assets/emblems/images/ALPHA_RAMPAGE.png";
import image_AETHER_WILDFIRE from "@/assets/emblems/images/AETHER_WILDFIRE.png";
import image_LEAVE_NO_WITNESSES from "@/assets/emblems/images/LEAVE_NO_WITNESSES.png";
import image_RIPTIDE from "@/assets/emblems/images/RIPTIDE.png";
import image_DORINTHEA from "@/assets/emblems/images/DORINTHEA.png";
import image_PRISM from "@/assets/emblems/images/PRISM.png";
import image_DROMAI from "@/assets/emblems/images/DROMAI.png";
import image_LEVIA from "@/assets/emblems/images/LEVIA.png";
import image_KAYO from "@/assets/emblems/images/KAYO.png";
import image_OLDHIM from "@/assets/emblems/images/OLDHIM.png";
import image_VYNNSET from "@/assets/emblems/images/VYNNSET.png";
import image_BRAVO from "@/assets/emblems/images/BRAVO.png";
import image_IYSLANDER from "@/assets/emblems/images/IYSLANDER.png";
import image_KASSAI from "@/assets/emblems/images/KASSAI.png";
import image_LEXI from "@/assets/emblems/images/LEXI.png";
import image_BRIAR from "@/assets/emblems/images/BRIAR.png";
import image_KAVDAEN from "@/assets/emblems/images/KAVDAEN.png";
import image_ARAKNI from "@/assets/emblems/images/ARAKNI.png";
import image_CHANE from "@/assets/emblems/images/CHANE.png";
import image_BENJI from "@/assets/emblems/images/BENJI.png";
import image_FAI from "@/assets/emblems/images/FAI.png";
import image_OLYMPIA from "@/assets/emblems/images/OLYMPIA.png";
import image_VICTOR from "@/assets/emblems/images/VICTOR.png";
import image_ANOTHOS from "@/assets/emblems/images/ANOTHOS.png";
import image_CINTARI_SABER from "@/assets/emblems/images/CINTARI_SABER.png";
import image_DAWNBLADE from "@/assets/emblems/images/DAWNBLADE.png";
import image_HARMONIZED_KODACHI from "@/assets/emblems/images/HARMONIZED_KODACHI.png";
import image_MANDIBLE_CLAW from "@/assets/emblems/images/MANDIBLE_CLAW.png";
import image_ROMPING_CLUB from "@/assets/emblems/images/ROMPING_CLUB.png";
import image_TEKLO_PLASMA_PISTOL from "@/assets/emblems/images/TEKLO_PLASMA_PISTOL.png";
import image_DEATH_DEALER from "@/assets/emblems/images/DEATH_DEALER.png";
import image_ROSETTA_THORN from "@/assets/emblems/images/ROSETTA_THORN.png";
import image_SEARING_EMBERBLADE from "@/assets/emblems/images/SEARING_EMBERBLADE.png";
import image_HELM_OF_ISENS_PEAK from "@/assets/emblems/images/HELM_OF_ISENS_PEAK.png";
import image_SINK_BELOW from "@/assets/emblems/images/SINK_BELOW.png";
import image_SNAPDRAGON_SCALERS from "@/assets/emblems/images/SNAPDRAGON_SCALERS.png";
import image_FATE_FORSEEN from "@/assets/emblems/images/FATE_FORSEEN.png";
import image_RAVENOUS_RABLE from "@/assets/emblems/images/RAVENOUS_RABLE.png";
import image_HALO_OF_ILLUMINATION from "@/assets/emblems/images/HALO_OF_ILLUMINATION.png";
import image_RAGAMUFFINS_HAT from "@/assets/emblems/images/RAGAMUFFINS_HAT.png";
import image_COPPER from "@/assets/emblems/images/COPPER.png";
import image_SPECTRAL_SHIELD from "@/assets/emblems/images/SPECTRAL_SHIELD.png";
import image_COLD_SNAP from "@/assets/emblems/images/COLD_SNAP.png";
import image_SCAR_FOR_A_SCAR from "@/assets/emblems/images/SCAR_FOR_A_SCAR.png";
import image_CROUCHING_TIGER from "@/assets/emblems/images/CROUCHING_TIGER.png";
import image_WATER_GLOW_LANTERNS from "@/assets/emblems/images/WATER_GLOW_LANTERNS.png";
import image_BIG_BERTHA from "@/assets/emblems/images/BIG_BERTHA.png";
import image_GOBLET_OF_BLOODRUN_WINE from "@/assets/emblems/images/GOBLET_OF_BLOODRUN_WINE.png";
import image_WILD_RIDE from "@/assets/emblems/images/WILD_RIDE.png";
import image_FIRST_TENET_OF_CHI_TIDE from "@/assets/emblems/images/FIRST_TENET_OF_CHI_TIDE.png";
import image_STIR_THE_POT from "@/assets/emblems/images/STIR_THE_POT.png";
import image_THE_GRAIN_THAT_TIPS_THE_SCALE from "@/assets/emblems/images/THE_GRAIN_THAT_TIPS_THE_SCALE.png";
import image_CRACKED_BAUBLE from "@/assets/emblems/images/CRACKED_BAUBLE.png";

import image_EYE_OF_OPHIDIA from "@/assets/emblems/images/EYE_OF_OPHIDIA.png";
import image_COMMAND_AND_CONQUER_2 from "@/assets/emblems/images/COMMAND_AND_CONQUER_2.png";
import image_DEAD_THREADS from "@/assets/emblems/images/DEAD_THREADS.png";
import image_POLLY_CRANKA from "@/assets/emblems/images/POLLY_CRANKA.png";
import image_SHIYANA_DIAMOND_GEMINI from "@/assets/emblems/images/SHIYANA_DIAMOND_GEMINI.png";
import image_CROWN_OF_PROVIDENCE from "@/assets/emblems/images/CROWN_OF_PROVIDENCE.png";
import image_CODEX_OF_FRAILTY from "@/assets/emblems/images/CODEX_OF_FRAILTY.png";
import image_CONQUEROR_OF_THE_HIGH_SEAS from "@/assets/emblems/images/CONQUEROR_OF_THE_HIGH_SEAS.png";
import image_DOWN_AND_DIRTY from "@/assets/emblems/images/DOWN_AND_DIRTY.png";
import image_DUST_FROM_THE_GOLDEN_PLAINS from "@/assets/emblems/images/DUST_FROM_THE_GOLDEN_PLAINS.png";
import image_ERASE_FACE from "@/assets/emblems/images/ERASE_FACE.png";
import image_FUNERAL_MOON from "@/assets/emblems/images/FUNERAL_MOON.png";
import image_JUDGE_JURY_EXECUTIONER from "@/assets/emblems/images/JUDGE_JURY_EXECUTIONER.png";
import image_RUBY_AMULET from "@/assets/emblems/images/RUBY_AMULET.png";
import image_SACRED_ART_UNDERCURRENT_DESIRES from "@/assets/emblems/images/SACRED_ART_UNDERCURRENT_DESIRES.png";
import image_TRUCE from "@/assets/emblems/images/TRUCE.png";
import image_AURORA from "@/assets/emblems/images/AURORA.png";
import image_AZALEA from "@/assets/emblems/images/AZALEA.png";
import image_BETSY from "@/assets/emblems/images/BETSY.png";
import image_CINDRA from "@/assets/emblems/images/CINDRA.png";
import image_FANG from "@/assets/emblems/images/FANG.png";
import image_FLORIAN from "@/assets/emblems/images/FLORIAN.png";
import image_GENIS_WOTCHUNEED from "@/assets/emblems/images/GENIS_WOTCHUNEED.png";
import image_GRAVY_BONES from "@/assets/emblems/images/GRAVY_BONES.png";
import image_IRA from "@/assets/emblems/images/IRA.png";
import image_KANO from "@/assets/emblems/images/KANO.png";
import image_MAXX_NITRO from "@/assets/emblems/images/MAXX_NITRO.png";
import image_NUU from "@/assets/emblems/images/NUU.png";
import image_OSCILIO from "@/assets/emblems/images/OSCILIO.png";
import image_PUFFIN from "@/assets/emblems/images/PUFFIN.png";
import image_SQUIZZY_AND_FLOOF from "@/assets/emblems/images/SQUIZZY_AND_FLOOF.png";
import image_TEKLOVOSSEN from "@/assets/emblems/images/TEKLOVOSSEN.png";
import image_UZURI from "@/assets/emblems/images/UZURI.png";
import image_VISERAI from "@/assets/emblems/images/VISERAI.png";
import image_YOJI from "@/assets/emblems/images/YOJI.png";
import image_ZEN from "@/assets/emblems/images/ZEN.png";
import image_A_DROP_IN_THE_OCEAN from "@/assets/emblems/images/A_DROP_IN_THE_OCEAN.png";
import image_AETHER_FLARE from "@/assets/emblems/images/AETHER_FLARE.png";
import image_ANGRY_BONES from "@/assets/emblems/images/ANGRY_BONES.png";
import image_BARBED_CASTAWAY from "@/assets/emblems/images/BARBED_CASTAWAY.png";
import image_BATTALION_BARQUE from "@/assets/emblems/images/BATTALION_BARQUE.png";
import image_BESEECH_THE_DEMIGON from "@/assets/emblems/images/BESEECH_THE_DEMIGON.png";
import image_BITING_BREEZE from "@/assets/emblems/images/BITING_BREEZE.png";
import image_CELESTIAL_REPRIMAND from "@/assets/emblems/images/CELESTIAL_REPRIMAND.png";
import image_CHART_A_COURSE from "@/assets/emblems/images/CHART_A_COURSE.png";
import image_CONSUMING_VOLITION from "@/assets/emblems/images/CONSUMING_VOLITION.png";
import image_DESIRES_OF_FLESH from "@/assets/emblems/images/DESIRES_OF_FLESH.png";
import image_DRY_POWDER_SHOT from "@/assets/emblems/images/DRY_POWDER_SHOT.png";
import image_EXPEDITION_TO_AZURO_KEYS from "@/assets/emblems/images/EXPEDITION_TO_AZURO_KEYS.png";
import image_FIRST_TENET_OF_CHI_MOON from "@/assets/emblems/images/FIRST_TENET_OF_CHI_MOON.png";
import image_FYENDALS_FIGHTING_SPIRIT from "@/assets/emblems/images/FYENDALS_FIGHTING_SPIRIT.png";
import image_HATCHET_OF_BODY from "@/assets/emblems/images/HATCHET_OF_BODY.png";
import image_HEAD_SHOT from "@/assets/emblems/images/HEAD_SHOT.png";
import image_HEAD_STONE from "@/assets/emblems/images/HEAD_STONE.png";
import image_IRIS_OF_REALITY from "@/assets/emblems/images/IRIS_OF_REALITY.png";
import image_KUNAI_OF_RETRIBUTION from "@/assets/emblems/images/KUNAI_OF_RETRIBUTION.png";
import image_MASK_OF_WIZENED_WHISKERS from "@/assets/emblems/images/MASK_OF_WIZENED_WHISKERS.png";
import image_POUNCING_QI from "@/assets/emblems/images/POUNCING_QI.png";
import image_RAVENOUS_MEATAXE from "@/assets/emblems/images/RAVENOUS_MEATAXE.png";
import image_ROTWOOD_REAPER from "@/assets/emblems/images/ROTWOOD_REAPER.png";
import image_SHIVER from "@/assets/emblems/images/SHIVER.png";
import image_SIGIL_OF_LIGHTNING from "@/assets/emblems/images/SIGIL_OF_LIGHTNING.png";
import image_STAR_FALL from "@/assets/emblems/images/STAR_FALL.png";
import image_STORM_OF_SANDIKAI from "@/assets/emblems/images/STORM_OF_SANDIKAI.png";
import image_TITANS_FIST from "@/assets/emblems/images/TITANS_FIST.png";
import image_WATER_THE_SEEDS from "@/assets/emblems/images/WATER_THE_SEEDS.png";

import image_ARKNIGHT_SHARD from "@/assets/emblems/images/ARKNIGHT_SHARD.png";
import image_MASK_OF_DECEIT from "@/assets/emblems/images/MASK_OF_DECEIT.png";
import image_CIRCLET_OF_ETERNAL_END from "@/assets/emblems/images/CIRCLET_OF_ETERNAL_END.png";
import image_COMMAND_AND_CONQUER_2026 from "@/assets/emblems/images/COMMAND_AND_CONQUER_2026.png";
import image_NEW_HORIZON from "@/assets/emblems/images/NEW_HORIZON.png";
import image_GHOSTLY_TOUCH from "@/assets/emblems/images/GHOSTLY_TOUCH.png";
import image_DIMENXXIONAL_VORTEX from "@/assets/emblems/images/DIMENXXIONAL_VORTEX.png";
import image_BRAINSTORM from "@/assets/emblems/images/BRAINSTORM.png";
import image_ANNIHILATOR_ENGINE from "@/assets/emblems/images/ANNIHILATOR_ENGINE.png";
import image_ART_OF_THE_DRAGON_CLAW from "@/assets/emblems/images/ART_OF_THE_DRAGON_CLAW.png";
import image_ACT_OF_GLORY from "@/assets/emblems/images/ACT_OF_GLORY.png";
import image_ATTUNE_WITH_COSMIC_VIBRATIONS from "@/assets/emblems/images/ATTUNE_WITH_COSMIC_VIBRATIONS.png";
import image_CARESS_OF_THE_REAPER from "@/assets/emblems/images/CARESS_OF_THE_REAPER.png";
import image_COAX_A_COMMOTION from "@/assets/emblems/images/COAX_A_COMMOTION.png";
import image_BOLTING_BLADE from "@/assets/emblems/images/BOLTING_BLADE.png";
import image_DIMENXXIONAL_CROSSROADS from "@/assets/emblems/images/DIMENXXIONAL_CROSSROADS.png";
import image_ECHO_CASQUE from "@/assets/emblems/images/ECHO_CASQUE.png";
import image_BANNERET_OF_RESILIENCE from "@/assets/emblems/images/BANNERET_OF_RESILIENCE.png";
import image_BLESSING_OF_SPIRITS from "@/assets/emblems/images/BLESSING_OF_SPIRITS.png";
import image_BASK_IN_YOUR_OWN_GREATNESS from "@/assets/emblems/images/BASK_IN_YOUR_OWN_GREATNESS.png";
import image_BAIT from "@/assets/emblems/images/BAIT.png";
import image_ART_OF_THE_DRAGON_SCALE from "@/assets/emblems/images/ART_OF_THE_DRAGON_SCALE.png";
import image_AGILITY_STANCE from "@/assets/emblems/images/AGILITY_STANCE.png";
import image_ANNEXATION_OF_ALL_THINGS_KNOWN from "@/assets/emblems/images/ANNEXATION_OF_ALL_THINGS_KNOWN.png";
import image_BLANCH from "@/assets/emblems/images/BLANCH.png";
import image_BENEATH_THE_SURFACE from "@/assets/emblems/images/BENEATH_THE_SURFACE.png";
import image_ARC_BENDING from "@/assets/emblems/images/ARC_BENDING.png";
import image_BLADE_BECKONER_HELM from "@/assets/emblems/images/BLADE_BECKONER_HELM.png";
import image_COPPER_COG from "@/assets/emblems/images/COPPER_COG.png";
import image_CLAP_EM_IN_IRONS from "@/assets/emblems/images/CLAP_EM_IN_IRONS.png";
import image_FOOLS_GOLD from "@/assets/emblems/images/FOOLS_GOLD.png";
import image_ARCANE_LANTERN from "@/assets/emblems/images/ARCANE_LANTERN.png";
import image_BLUSTER_BUFF from "@/assets/emblems/images/BLUSTER_BUFF.png";
import image_FIDDLE_DEE from "@/assets/emblems/images/FIDDLE_DEE.png";
import image_AETHER_SINK from "@/assets/emblems/images/AETHER_SINK.png";
import image_BLACKOUT_KICK from "@/assets/emblems/images/BLACKOUT_KICK.png";
import image_BACKUP_PROTOCOL_RED from "@/assets/emblems/images/BACKUP_PROTOCOL_RED.png";
import image_ANNIHILATE_THE_ARMED from "@/assets/emblems/images/ANNIHILATE_THE_ARMED.png";
import image_APHRODIAS from "@/assets/emblems/images/APHRODIAS.png";
import image_VANTOM_BANSHEE from "@/assets/emblems/images/VANTOM_BANSHEE.png";
import image_SOUL_BUTCHER from "@/assets/emblems/images/SOUL_BUTCHER.png";
import image_TEAR_THROUGH_THE_PORTAL from "@/assets/emblems/images/TEAR_THROUGH_THE_PORTAL.png";
import image_WITHSTAND from "@/assets/emblems/images/WITHSTAND.png";
import image_SKY_FIRE_LANTERNS from "@/assets/emblems/images/SKY_FIRE_LANTERNS.png";
import image_SPECTRAL_RIDER from "@/assets/emblems/images/SPECTRAL_RIDER.png";
import image_WANING_VENGEANCE from "@/assets/emblems/images/WANING_VENGEANCE.png";
import image_SPILLOVER from "@/assets/emblems/images/SPILLOVER.png";
import image_ZOOM_IN from "@/assets/emblems/images/ZOOM_IN.png";
import image_STABBING_PAIN from "@/assets/emblems/images/STABBING_PAIN.png";
import image_VOW_OF_VENGEANCE from "@/assets/emblems/images/VOW_OF_VENGEANCE.png";
import image_STARLIGHT_ROAD from "@/assets/emblems/images/STARLIGHT_ROAD.png";
import image_VIRULENT_TOUCH from "@/assets/emblems/images/VIRULENT_TOUCH.png";
import image_SPRING_LOAD from "@/assets/emblems/images/SPRING_LOAD.png";
import image_BILLOWING_MIST from "@/assets/emblems/images/BILLOWING_MIST.png";
import image_SPREADING_MIST from "@/assets/emblems/images/SPREADING_MIST.png";
import image_TWO_SIDES_TO_THE_BLADE from "@/assets/emblems/images/TWO_SIDES_TO_THE_BLADE.png";
import image_STROKE_OF_FORESIGHT from "@/assets/emblems/images/STROKE_OF_FORESIGHT.png";
import image_BANDANA_OF_THE_BLUE_BEYOND from "@/assets/emblems/images/BANDANA_OF_THE_BLUE_BEYOND.png";
import image_WALK_THE_PLANK from "@/assets/emblems/images/WALK_THE_PLANK.png";
import image_SWIFTWATER_SLOOP from "@/assets/emblems/images/SWIFTWATER_SLOOP.png";
import image_WEE_WRECKING_BALL from "@/assets/emblems/images/WEE_WRECKING_BALL.png";
import image_ARENA_MEDIC from "@/assets/emblems/images/ARENA_MEDIC.png";
import image_TO_BE_CONTINUED from "@/assets/emblems/images/TO_BE_CONTINUED.png";
import image_AMPLIFY_THE_ARKNIGHT from "@/assets/emblems/images/AMPLIFY_THE_ARKNIGHT.png";
import image_SONG_OF_THE_ROSEN_MATADOR from "@/assets/emblems/images/SONG_OF_THE_ROSEN_MATADOR.png";
import image_SURGING_MILITIA from "@/assets/emblems/images/SURGING_MILITIA.png";
